/**
 * ScheduleScreen — Driver Portal schedule & PTO module
 * Extracted from DriverApp.jsx (V2-03.12j, Micro 10)
 *
 * Reads: /api/driver/schedule, /api/driver/time-off, /api/driver/pto-balance
 * Writes: POST /api/driver/time-off (new request)
 */

import React, { useState, useEffect } from 'react';
import { C, FONT, GLASS, GLASS_STRONG, INPUT, BTN, BTN_DISABLED } from '../driver-tokens.js';
import { api, apiPost } from '../auth/driver-session.js';

interface Assignment {
  id: string | number;
  assignment_type?: string;
  started_at?: string;
  ended_at?: string;
  terminals?: { name?: string; fedex_number?: string };
}

interface TimeOffRequest {
  id: string | number;
  request_type: string;
  start_date: string;
  end_date: string;
  status: string;
  reason?: string;
  decision_notes?: string;
}

interface PtoInfo {
  eligible?: boolean;
  balance?: { remaining: number; annual_days: number };
}

interface Feedback { type: string; msg: string }

function ScheduleScreen() {
  var [assignments, setAssignments] = useState<Assignment[]>([]);
  var [loading, setLoading] = useState(true);
  var [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[]>([]);
  var [ptoInfo, setPtoInfo] = useState<PtoInfo | null>(null);
  var [showForm, setShowForm] = useState(false);
  var [toType, setToType] = useState("sick");
  var [toStart, setToStart] = useState("");
  var [toEnd, setToEnd] = useState("");
  var [toReason, setToReason] = useState("");
  var [submitting, setSubmitting] = useState(false);
  var [feedback, setFeedback] = useState<Feedback | null>(null);

  useEffect(function() {
    Promise.all([
      api<{ assignments?: Assignment[] }>("/api/driver/schedule"),
      api<{ requests?: TimeOffRequest[] }>("/api/driver/time-off"),
      api<PtoInfo>("/api/driver/pto-balance"),
    ]).then(function(results) {
      setAssignments(results[0].assignments || []);
      setTimeOffRequests(results[1].requests || []);
      setPtoInfo(results[2]);
      setLoading(false);
    });
  }, []);

  function refreshTimeOff() {
    api<{ requests?: TimeOffRequest[] }>("/api/driver/time-off").then(function(data) {
      setTimeOffRequests(data.requests || []);
    });
    api<PtoInfo>("/api/driver/pto-balance").then(function(data) {
      setPtoInfo(data);
    });
  }

  function handleSubmit() {
    if (!toType || !toStart || !toEnd) return;
    setSubmitting(true);
    setFeedback(null);
    apiPost<{ error?: string }>("/api/driver/time-off", {
      request_type: toType,
      start_date: toStart,
      end_date: toEnd,
      reason: toReason || null
    }).then(function(data) {
      setSubmitting(false);
      if (data.error) {
        setFeedback({ type: "error", msg: data.error });
      } else {
        setFeedback({ type: "success", msg: "Request submitted" });
        setShowForm(false);
        setToType("sick");
        setToStart("");
        setToEnd("");
        setToReason("");
        refreshTimeOff();
      }
    }).catch(function() {
      setSubmitting(false);
      setFeedback({ type: "error", msg: "Network error" });
    });
  }

  var TYPE_LABELS: Record<string, string> = { pto: "PTO", sick: "Sick", personal: "Personal", rotation_off: "Rotation Off", other: "Other" };
  var STATUS_COLORS: Record<string, string> = { pending: C.orange, approved: C.green, denied: C.red };
  var today = new Date().toISOString().split("T")[0];
  var canSubmit = toType && toStart && toEnd && toEnd >= toStart && toStart >= today && !submitting;

  if (loading) {
    return React.createElement("div", { style: { padding: 20, paddingBottom: 100 } },
      React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 14, color: C.silver, textAlign: "center", padding: 40 } }, "Loading...")
    );
  }

  return React.createElement("div", { style: { padding: 20, paddingBottom: 100 } },
    React.createElement("div", { style: { fontFamily: FONT.headline, fontSize: 20, color: C.text, marginBottom: 16 } }, "Schedule"),

    // ---------- Request Time Off Button / Form ----------
    !showForm && React.createElement("button", {
      style: { ...BTN, marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
      onClick: function() { setShowForm(true); setFeedback(null); }
    }, "\uD83D\uDCC5 Request Time Off"),

    showForm && React.createElement("div", { style: { ...GLASS_STRONG, padding: 20, marginBottom: 16, borderRadius: 14 } },
      React.createElement("div", { style: { fontFamily: FONT.body, fontSize: 15, color: C.text, fontWeight: 600, marginBottom: 12 } }, "Request Time Off"),

      // Type pills
      React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 } }, "Type"),
      React.createElement("div", { style: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 } },
        ["sick", "personal", "other"].concat(ptoInfo && ptoInfo.eligible ? ["pto"] : []).map(function(t) {
          var active = toType === t;
          return React.createElement("button", {
            key: t,
            style: {
              padding: "8px 16px", borderRadius: 20, border: "1px solid " + (active ? C.gold : "rgba(255,255,255,0.1)"),
              background: active ? C.gold : "rgba(255,255,255,0.04)", color: active ? C.navy : C.silver,
              fontFamily: FONT.mono, fontSize: 12, fontWeight: active ? 700 : 400, cursor: "pointer", minHeight: 36
            },
            onClick: function() { setToType(t); }
          }, TYPE_LABELS[t]);
        })
      ),

      // PTO balance indicator
      toType === "pto" && ptoInfo && ptoInfo.balance && React.createElement("div", {
        style: { ...GLASS, padding: 10, borderRadius: 8, marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }
      },
        React.createElement("span", { style: { fontFamily: FONT.mono, fontSize: 12, color: C.silver } }, "PTO Balance"),
        React.createElement("span", { style: { fontFamily: FONT.mono, fontSize: 13, fontWeight: 700, color: ptoInfo.balance.remaining >= 3 ? C.gold : ptoInfo.balance.remaining >= 1 ? C.orange : C.red } },
          ptoInfo.balance.remaining + "/" + ptoInfo.balance.annual_days + " days"
        )
      ),

      !ptoInfo || !ptoInfo.eligible ? React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, marginBottom: 6 } },
        "PTO requires 1+ year tenure"
      ) : null,

      // Date inputs
      React.createElement("div", { style: { display: "flex", gap: 10, marginBottom: 14 } },
        React.createElement("div", { style: { flex: 1 } },
          React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 } }, "Start"),
          React.createElement("input", { type: "date", value: toStart, min: today, style: { ...INPUT, fontSize: 14 }, onChange: function(e) { setToStart(e.target.value); if (!toEnd || e.target.value > toEnd) setToEnd(e.target.value); } })
        ),
        React.createElement("div", { style: { flex: 1 } },
          React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 } }, "End"),
          React.createElement("input", { type: "date", value: toEnd, min: toStart || today, style: { ...INPUT, fontSize: 14 }, onChange: function(e) { setToEnd(e.target.value); } })
        )
      ),

      // Reason
      React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 } }, "Reason (optional)"),
      React.createElement("textarea", {
        value: toReason, rows: 2,
        style: { ...INPUT, fontSize: 14, resize: "vertical", minHeight: 60 },
        onChange: function(e: React.ChangeEvent<HTMLTextAreaElement>) { setToReason(e.target.value); }
      }),

      // Feedback
      feedback && React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 13, color: feedback.type === "error" ? C.red : C.green, marginTop: 10, textAlign: "center" } }, feedback.msg),

      // Buttons
      React.createElement("div", { style: { display: "flex", gap: 10, marginTop: 14 } },
        React.createElement("button", {
          style: { ...GLASS, flex: 1, padding: "12px", color: C.silver, fontFamily: FONT.mono, fontSize: 14, cursor: "pointer", border: "1px solid rgba(255,255,255,0.08)", minHeight: 48, textAlign: "center" },
          onClick: function() { setShowForm(false); setFeedback(null); }
        }, "Cancel"),
        React.createElement("button", {
          style: canSubmit ? { ...BTN, flex: 2 } : { ...BTN_DISABLED, flex: 2 },
          disabled: !canSubmit,
          onClick: handleSubmit
        }, submitting ? "Submitting..." : "Submit Request")
      )
    ),

    // ---------- My Requests ----------
    timeOffRequests.length > 0 && React.createElement("div", { style: { marginBottom: 20 } },
      React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 } }, "My Requests"),
      timeOffRequests.map(function(r) {
        return React.createElement("div", { key: r.id, style: { ...GLASS, padding: 14, marginBottom: 8, borderRadius: 10 } },
          React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 } },
            React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8 } },
              React.createElement("span", { style: {
                fontFamily: FONT.mono, fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 8,
                background: "rgba(255,255,255,0.06)", color: C.text
              } }, TYPE_LABELS[r.request_type] || r.request_type),
              React.createElement("span", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.silver } },
                new Date(r.start_date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) +
                (r.end_date !== r.start_date ? " \u2013 " + new Date(r.end_date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "")
              )
            ),
            React.createElement("span", { style: {
              fontFamily: FONT.mono, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 8,
              background: (STATUS_COLORS[r.status] || C.silver) + "22", color: STATUS_COLORS[r.status] || C.silver,
              textTransform: "uppercase"
            } }, r.status)
          ),
          r.reason && React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, marginTop: 4 } }, r.reason),
          r.status === "denied" && r.decision_notes && React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.red, marginTop: 4 } }, "Denied: " + r.decision_notes)
        );
      })
    ),

    // ---------- Assignments ----------
    React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 } }, "Assignment History"),
    assignments.length === 0 ? React.createElement("div", { style: { ...GLASS, padding: 32, textAlign: "center" } },
      React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 14, color: C.textMuted } }, "No assignments found")
    ) : assignments.map(function(a) {
      return React.createElement("div", { key: a.id, style: { ...GLASS, padding: 16, marginBottom: 10 } },
        React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center" } },
          React.createElement("div", null,
            React.createElement("div", { style: { fontFamily: FONT.body, fontSize: 15, color: C.text, fontWeight: 600 } }, a.terminals ? a.terminals.name : "Home"),
            React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.silver } }, a.assignment_type + " \u2022 " + (a.terminals ? a.terminals.fedex_number : ""))
          ),
          React.createElement("div", { style: { textAlign: "right" } },
            React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted } }, new Date(a.started_at || "").toLocaleDateString()),
            a.ended_at && React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 10, color: C.red } }, "Ended " + new Date(a.ended_at).toLocaleDateString())
          )
        )
      );
    })
  );
}

export default ScheduleScreen;
